import React, { useEffect, useMemo, useState } from 'react';  
import { useNavigate } from 'react-router-dom';  
import {  
  FiAlertCircle,  
  FiBell,  
  FiCreditCard,  
  FiFileText,  
  FiRefreshCw,  
  FiTrendingUp,  
} from 'react-icons/fi';  
import { portalAPI } from '../../services/api';  
import { usePortalAuth } from '../../context/PortalAuthContext';  
  
const formatCurrency = (value) => `${Number(value || 0).toLocaleString('fr-FR')} XOF`;  
  
const PortalDashboardPage = () => {  
  const { user } = usePortalAuth();  
  const navigate = useNavigate();  
  
  const [loading, setLoading] = useState(true);  
  const [invoices, setInvoices] = useState([]);  
  const [leasePayments, setLeasePayments] = useState([]);  
  const [notifications, setNotifications] = useState([]);  
  const [unreadCount, setUnreadCount] = useState(0);
  const [intents, setIntents] = useState([]);  
  
  const role = user?.role_slug || user?.role || '';  
  const isLocataire = role === 'locataire';  
  
  const fetchData = async () => {  
    setLoading(true);  
    try {  
      const [invoiceRes, paymentRes, notifRes] = await Promise.all([  
        portalAPI.billing.listInvoices({ limit: 5 }),  
        isLocataire  
          ? portalAPI.billing.listLeasePayments({ limit: 5 })  
          : Promise.resolve({ data: [] }),  
        portalAPI.notifications.list({ limit: 5 }),  
      ]);  
  
      const invoiceData = Array.isArray(invoiceRes.data)  
        ? invoiceRes.data  
        : invoiceRes.data?.items || [];  
      const paymentData = Array.isArray(paymentRes.data)  
        ? paymentRes.data  
        : paymentRes.data?.items || [];  
      const notifData = Array.isArray(notifRes.data?.notifications)  
        ? notifRes.data.notifications  
        : Array.isArray(notifRes.data)  
          ? notifRes.data  
          : [];  
  
      setInvoices(invoiceData);  
      setLeasePayments(paymentData);  
      setNotifications(notifData);  
      setUnreadCount(notifRes.data?.unread_count || 0);  
    } catch (error) {  
      setInvoices([]);  
      setLeasePayments([]);  
      setNotifications([]);  
      setUnreadCount(0);  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  useEffect(() => {  
    fetchData();  
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, []);  
  
  const summary = useMemo(() => {  
    const invoiceTotal = invoices.reduce((sum, item) => sum + Number(item.total || 0), 0);  
    const pendingInvoices = invoices.filter((item) => ['draft', 'sent', 'pending'].includes(item.status)).length;  
    const overdueInvoices = invoices.filter((item) => item.status === 'overdue').length;  
    const duePayments = leasePayments.filter((item) => item.status === 'pending').length;  
  
    return {  
      invoiceTotal,  
      pendingInvoices,  
      overdueInvoices,  
      duePayments,  
    };  
  }, [invoices, leasePayments]);  
  
  const recentInvoices = invoices.slice(0, 5);  
  const recentPayments = leasePayments.slice(0, 5);  
  
  return (  
    <div>  
      <div className="page-header">  
        <div>  
          <h1>Tableau de bord portail</h1>  
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>  
            Consultez vos factures, paiements et notifications.  
          </p>  
        </div>  
  
        <button className="btn btn-secondary" onClick={fetchData}>  
          <FiRefreshCw />  
          Actualiser  
        </button>  
      </div>  
  
      <div className="grid-4" style={{ marginBottom: 20 }}>  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Factures visibles</div>  
            <div className="stat-value">{recentInvoices.length}</div>  
          </div>  
        </div>  
  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Total facturé</div>  
            <div className="stat-value">{formatCurrency(summary.invoiceTotal)}</div>  
          </div>  
        </div>  
  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Échéances en attente</div>  
            <div className="stat-value">{summary.pendingInvoices + summary.duePayments}</div>  
          </div>  
        </div>  
  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Notifications non lues</div>  
            <div className="stat-value">{unreadCount}</div>  
          </div>  
        </div>  
      </div>  
  
      <div className="grid-2" style={{ marginBottom: 20 }}>  
        <div className="card">  
          <div className="card-header">  
            <h2>  
              <FiTrendingUp /> Vue rapide  
            </h2>  
          </div>  
          <div className="card-body">  
            <div className="detail-row">  
              <div className="detail-label">Factures en attente</div>  
              <div className="detail-value">{summary.pendingInvoices}</div>  
            </div>  
            <div className="detail-row">  
              <div className="detail-label">Factures en retard</div>  
              <div className="detail-value">{summary.overdueInvoices}</div>  
            </div>  
            {isLocataire && (  
              <div className="detail-row">  
                <div className="detail-label">Paiements à venir</div>  
                <div className="detail-value">{summary.duePayments}</div>  
              </div>  
            )}  
          </div>  
        </div>  
  
        <div className="card">  
          <div className="card-header">  
            <h2>  
              <FiBell /> Notifications récentes  
            </h2>  
          </div>  
          <div className="card-body">  
            {notifications.length === 0 ? (  
              <div className="empty-state">  
                <FiAlertCircle />  
                <h3>Aucune notification</h3>  
              </div>  
            ) : (  
              <div style={{ display: 'grid', gap: 12 }}>  
                {notifications.map((notif) => (  
                  <div key={notif.id} className="detail-row">  
                    <div>  
                      <div className="detail-label">{notif.title}</div>  
                      <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>  
                        {notif.message}  
                      </div>  
                    </div>  
                    {!notif.is_read && <span className="badge badge-info">Nouveau</span>}  
                  </div>  
                ))}  
              </div>  
            )}  
          </div>  
        </div>  
      </div>  
  
      <div className="grid-2">  
        <div className="card">  
          <div className="card-header">  
            <h2>  
              <FiFileText /> Factures récentes  
            </h2>  
          </div>  
          <div className="card-body">  
            {recentInvoices.length === 0 ? (  
              <div className="empty-state">  
                <FiFileText />  
                <h3>Aucune facture</h3>  
              </div>  
            ) : (  
              <div style={{ display: 'grid', gap: 12 }}>  
                {recentInvoices.map((invoice) => (  
                  <button  
                    key={invoice.id}  
                    className="detail-row"  
                    style={{ cursor: 'pointer', width: '100%', textAlign: 'left' }}  
                    onClick={() => navigate(`/portal/invoices/${invoice.id}`)}  
                  >  
                    <div>  
                      <div className="detail-label">{invoice.invoice_number || invoice.id.slice(0, 8)}</div>  
                      <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>  
                        Échéance: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('fr-FR') : '-'}  
                      </div>  
                    </div>  
                    <div style={{ textAlign: 'right' }}>  
                      <div className="detail-value">{formatCurrency(invoice.total)}</div>  
                      <span className={`badge badge-${  
                        invoice.status === 'paid' ? 'success'  
                          : invoice.status === 'overdue' ? 'danger'  
                            : invoice.status === 'sent' ? 'info' : 'secondary'  
                      }`}>  
                        {invoice.status || 'draft'}  
                      </span>  
                    </div>  
                  </button>  
                ))}  
              </div>  
            )}  
          </div>  
        </div>  
  
        <div className="card">  
          <div className="card-header">  
            <h2>  
              <FiCreditCard /> Paiements récents  
            </h2>  
          </div>  
          <div className="card-body">  
            {!isLocataire ? (  
              <div className="empty-state">  
                <FiCreditCard />  
                <h3>Historique réservé au locataire</h3>  
              </div>  
            ) : recentPayments.length === 0 ? (  
              <div className="empty-state">  
                <FiCreditCard />  
                <h3>Aucun paiement</h3>  
              </div>  
            ) : (  
              <div style={{ display: 'grid', gap: 12 }}>  
                {recentPayments.map((payment) => (  
                  <div key={payment.id} className="detail-row">  
                    <div>  
                      <div className="detail-label">  
                        {payment.reference || payment.contract_number || payment.id.slice(0, 8)}  
                      </div>  
                      <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>  
                        Échéance: {payment.due_date ? new Date(payment.due_date).toLocaleDateString('fr-FR') : '-'}  
                      </div>  
                    </div>  
                    <div style={{ textAlign: 'right' }}>  
                      <div className="detail-value">{formatCurrency(payment.amount)}</div>  
                      <span className={`badge badge-${payment.status === 'paid' ? 'success' : 'warning'}`}>  
                        {payment.status || 'pending'}  
                      </span>  
                    </div>  
                  </div>  
                ))}  
              </div>  
            )}  
          </div>  
        </div>  
      </div>  
    </div>  
  );  
};  
  
export default PortalDashboardPage;